import type { NoeudGroupe } from '@admin-pmvb/core'
import { type ReactNode, useState } from 'react'
import { Icone } from './Icone'
import { LogoConnu } from './Logo'
import { Interrupteur } from './Ui'

/** Éléments marqués d'un groupe, sous-groupes compris. */
function compterMarques<T>(noeuds: readonly NoeudGroupe<T>[], est: (e: T) => boolean): number {
  return noeuds.reduce(
    (n, x) => n + ('element' in x ? (est(x.element) ? 1 : 0) : compterMarques(x.enfants, est)),
    0,
  )
}

/**
 * Liste regroupée en arbre (tiers puis sorte) : chaque groupe a son en-tête repliable avec
 * son nombre, ses lignes en retrait dessous. Les lignes restent celles de la liste à plat.
 * `marque` : ce que l'en-tête signale de ses lignes (« 2 nouvelles »), même groupe replié.
 */
export function ListeGroupee<T>({
  noeuds,
  ligne,
  cle,
  marque,
  profondeur = 0,
}: {
  noeuds: readonly NoeudGroupe<T>[]
  ligne: (element: T) => ReactNode
  cle: (element: T) => string
  marque?: { est: (element: T) => boolean; libelle: (nombre: number) => string }
  profondeur?: number
}) {
  return (
    <>
      {noeuds.map((n) =>
        'element' in n ? (
          <div key={cle(n.element)} className="groupe-ligne" data-profondeur={profondeur}>
            {ligne(n.element)}
          </div>
        ) : (
          <details key={`g:${n.cle}`} className="groupe-liste" data-profondeur={profondeur} open>
            <summary className="groupe-entete">
              <Icone nom="chevron_right" className="groupe-chevron" />
              <LogoConnu nom={n.libelle} taille={22} />
              <span className="groupe-libelle">{n.libelle}</span>
              {marque && (
                <MarqueGroupe nombre={compterMarques(n.enfants, marque.est)} marque={marque} />
              )}
              <span className="mono groupe-total">{n.total}</span>
            </summary>
            <ListeGroupee
              noeuds={n.enfants}
              ligne={ligne}
              cle={cle}
              {...(marque && { marque })}
              profondeur={profondeur + 1}
            />
          </details>
        ),
      )}
    </>
  )
}

function MarqueGroupe({
  nombre,
  marque,
}: {
  nombre: number
  marque: { libelle: (nombre: number) => string }
}) {
  if (nombre === 0) return null
  return <span className="badge plein-accent groupe-marque">{marque.libelle(nombre)}</span>
}

/** Regrouper ou non : préférence de l'appareil, regroupé par défaut. */
export function useRegroupement(liste: string): [boolean, (v: boolean) => void] {
  const cle = `regrouper:${liste}`
  const [actif, setActif] = useState(() => {
    try {
      return window.localStorage.getItem(cle) !== 'non'
    } catch {
      return true
    }
  })
  function changer(v: boolean) {
    setActif(v)
    try {
      window.localStorage.setItem(cle, v ? 'oui' : 'non')
    } catch {
      // Stockage indisponible (navigation privée) : la préférence vaut pour la page.
    }
  }
  return [actif, changer]
}

export function BasculeRegroupement({
  actif,
  onChange,
}: {
  actif: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <Interrupteur actif={actif} onChange={onChange}>
      Grouper
    </Interrupteur>
  )
}
